import React from 'react';
import classNames from 'classnames';
import { Field } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import validations from 'validations';
import HintMessage from 'components/HintMessage';
import ToggleField from 'components/ToggleField';
import ListingFieldRequiredVisibleInfo from 'router/subrouters/ListingCreationWizard/components/ListingFieldRequiredVisibleInfo';
import { getFieldValidations } from 'validations/general';
import useListingFieldIsRequired from 'router/subrouters/ListingCreationWizard/hooks/useListingFieldIsRequired';
import { REQUIRED_VISIBLE_INFO } from 'data/global';
import AiDraftBanner, {
  AI_DRAFT_BANNER_STEP,
} from 'router/subrouters/ListingCreationWizard/components/AiDraftBanner';

import './StatementOfInformation.styles.scss';

type StatementOfInformationProps = {
  className?: string;
};

const StatementOfInformation: React.FC<StatementOfInformationProps> = (
  props,
) => {
  const { className } = props;

  const classes = classNames('statement-of-information', 'lcw-page', className);

  const { t } = useTranslation();

  const [isRevenueRequired, isRevenueDefaultRequired] =
    useListingFieldIsRequired('revenue', true);
  const [isEBITDARequired, isEBITDADefaultRequired] = useListingFieldIsRequired(
    'EBITDA',
    true,
  );
  const [isAverageStockValueRequired, isAverageStockValueDefaultRequired] =
    useListingFieldIsRequired('averageStockValue', true);

  return (
    <div className={classes}>
      <p className="lcw-page__heading__title">{t('statementOfInformation')}</p>

      <AiDraftBanner step={AI_DRAFT_BANNER_STEP.STATEMENT_OF_INFORMATION} />

      <HintMessage
        message={
          <div className="statement-of-information__hint-message">
            <p>
              {t('noDetailsOnTheStatementOfInformationIdentifyTheBusiness')}
            </p>
            <p>{t('soiCriteriaMessage')}</p>
            <ul>
              <li>{t('areLoggedIn')}</li>
              <li>{t('haveVerifiedTherAccount')}</li>
              <li>{t('haveEnquiredOnTheListing')}</li>
            </ul>
            <p>
              {t(
                'priceInformationEnablesResolveToMatchListingsToPriceFilteredSearch',
              )}
            </p>
          </div>
        }
      />

      <Field
        name="revenue"
        label={t('revenue')}
        labelChildren={
          <ListingFieldRequiredVisibleInfo
            fieldName={`revenue${REQUIRED_VISIBLE_INFO}`}
            fieldLabel={t('revenue')}
            isDefaultRequired={isRevenueDefaultRequired}
          />
        }
        numberStyle="dottedNumber"
        component={InputField}
        placeholder="$"
        validate={validations.positiveNumbersOnly(
          t('onlyPositiveNumbersAreAllowed'),
        )}
        description={t(
          'useEitherTheMostRecentFullCalendarYearFinancialYearOrAverageOfLastTwoYears',
        )}
        parse={(val) => val}
        {...getFieldValidations(isRevenueRequired, [
          validations.positiveNumbersOnly(),
        ])}
      />
      <Field
        name="EBITDA"
        label={t('ebitda')}
        labelChildren={
          <ListingFieldRequiredVisibleInfo
            fieldName={`EBITDA${REQUIRED_VISIBLE_INFO}`}
            fieldLabel={t('ebitda')}
            isDefaultRequired={isEBITDADefaultRequired}
          />
        }
        numberStyle="dottedNumber"
        component={InputField}
        placeholder="$"
        description={t(
          'useEitherTheMostRecentFullCalendarYearFinancialYearOrAverageOfLastTwoYears',
        )}
        parse={(val) => val}
        {...getFieldValidations(isEBITDARequired, [
          validations.positiveNumbersOnly(),
        ])}
      />
      <Field
        name="averageStockValue"
        label={t('averageStockValue')}
        labelChildren={
          <ListingFieldRequiredVisibleInfo
            fieldName={`averageStockValue${REQUIRED_VISIBLE_INFO}`}
            fieldLabel={t('averageStockValue')}
            isDefaultRequired={isAverageStockValueDefaultRequired}
          />
        }
        numberStyle="dottedNumber"
        component={InputField}
        placeholder="$"
        description={t(
          'useEitherTheMostRecentFullCalendarYearFinancialYearOrAverageOfLastTwoYears',
        )}
        parse={(val) => val}
        {...getFieldValidations(isAverageStockValueRequired, [
          validations.positiveNumbersOnly(),
        ])}
      />

      <Field
        name="purchaseFreeholdCommercialProperty"
        label={t('optionToPurchaseFreeholdCommercialProperty')}
        type="checkbox"
        className="black-toggle"
        component={ToggleField}
      />
    </div>
  );
};

export default StatementOfInformation;
